import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Typography, Box, Button, CircularProgress } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { api } from '../../../api';
import { localGet } from '../../../utils/localStorageUtils';
import { removedNamesForUpdate } from '../../../utils/utilsApi';

/**
 * Parent Access add-on (Oct 2026). A paid add-on, OFF for every district at onboarding. Only
 * DiscloSure admins can turn it on or off (the server enforces this too and records who/when).
 * Everyone else sees whether it's on.
 */
const ParentAccessPanel = ({ data }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [enabled, setEnabled] = useState(String(data?.parentAccessEnabled) === 'True');
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => setUser(await localGet('user', true)))();
  }, []);

  const canChange = user?.permission === 'disclosureAdmin';

  const apply = async () => {
    setBusy(true); setError('');
    const next = enabled ? 'False' : 'True';
    const updated = { ...data, parentAccessEnabled: next };
    const record = await removedNamesForUpdate({ ...updated });
    const res = await api('patch', record, 'districts');
    if (res?.status === 200) {
      setEnabled(next === 'True');
      navigate('.', { replace: true, state: updated });
    } else {
      setError(res?.data?.message || 'Could not change Parent Access. Please try again.');
    }
    setConfirming(false);
    setBusy(false);
  };

  const style = {
    container: {
      display: 'flex', flexDirection: 'column', marginLeft: '60px', marginRight: '60px', marginTop: '40px',
      width: 'auto', paddingLeft: '0px !important', border: `1px solid ${theme.palette.custom?.gray || '#ccc'}`, borderRadius: '4px',
    },
    header: { borderBottom: `1px solid ${theme.palette.custom?.gray || '#ccc'}`, display: 'flex', alignItems: 'center', gap: '12px' },
    title: { fontSize: '20px', fontWeight: '700', marginTop: '20px', marginBottom: '20px', marginLeft: '20px' },
    badge: (on) => ({
      fontSize: '12px', fontWeight: 700, padding: '2px 10px', borderRadius: '999px',
      color: on ? '#1b5e20' : '#5D6673', background: on ? '#e5f4e4' : '#f0efec',
    }),
    body: { padding: '20px', display: 'flex', flexDirection: 'column', gap: '12px' },
    text: { fontSize: '14px', color: theme.palette.text?.secondary || '#666', lineHeight: 1.5 },
    confirm: { border: `1px solid ${theme.palette.warning?.main || '#ed6c02'}`, borderRadius: '4px', padding: '12px', display: 'flex', flexDirection: 'column', gap: '10px' },
    error: { fontSize: '13px', color: theme.palette.error?.main || '#d32f2f' },
  };

  return (
    <Container sx={style.container}>
      <Box sx={style.header}>
        <Typography sx={style.title}>Parent Access</Typography>
        <Typography sx={style.badge(enabled)}>{enabled ? 'On' : 'Off'}</Typography>
      </Box>
      <Box sx={style.body}>
        <Typography sx={style.text}>
          A paid add-on. When it's on, schools in this district can invite a student's parent to view that
          student's DiscloSure account (view-only). Home-related reports are always hidden from parents.
        </Typography>
        {!canChange ? (
          <Typography sx={style.text}>Only DiscloSure can turn Parent Access on or off.</Typography>
        ) : confirming ? (
          <Box sx={style.confirm}>
            <Typography sx={{ fontWeight: 700 }}>
              {enabled ? 'Turn Parent Access off for this district?' : 'Turn Parent Access on for this district?'}
            </Typography>
            <Typography sx={style.text}>
              {enabled
                ? 'Linked parents lose access immediately. Their links are kept, so turning it back on restores them.'
                : 'Make sure the district has purchased the Parent Access add-on. Schools will be able to invite parents.'}
            </Typography>
            <Box sx={{ display: 'flex', gap: '12px' }}>
              <Button variant="contained" color={enabled ? 'error' : 'primary'} disabled={busy} onClick={apply}>
                {busy ? <CircularProgress size={20} /> : enabled ? 'Turn off' : 'Turn on'}
              </Button>
              <Button variant="outlined" color="inherit" disabled={busy} onClick={() => setConfirming(false)}>Cancel</Button>
            </Box>
          </Box>
        ) : (
          <Box>
            <Button variant={enabled ? 'outlined' : 'contained'} onClick={() => setConfirming(true)}>
              {enabled ? 'Turn off Parent Access' : 'Turn on Parent Access'}
            </Button>
          </Box>
        )}
        {error ? <Typography sx={style.error}>{error}</Typography> : null}
      </Box>
    </Container>
  );
};

export default ParentAccessPanel;
